import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { routedClient } from "react-migration/lib/persistence/apollo";
import { Title } from "../../typings/Title";
import { Nullable } from "src/js/types/Nullable";
import { titlesByLocation } from ".";

export interface TitlesByLocationQueryData {
  titlesByLocation: Title[];
}

export type OwnershipIntersectableGeometry =
  | GeoJSON.MultiPolygon
  | GeoJSON.Polygon
  | GeoJSON.Point
  | GeoJSON.MultiPoint;

export interface TitlesByLocationQueryVariables {
  location: OwnershipIntersectableGeometry;
  limit?: number;
}

/**
 * Takes a clicked geometry and returns a list of titles
 * @param options - Configuration options
 * @param options.location - The geometry to query titles for
 * @param options.limit - Maximum number of titles to return
 * @returns { titlesByLocation, queryResult, loading }
 */
export interface UseTitlesByLocationQueryOptions {
  location?: Nullable<OwnershipIntersectableGeometry>;
  limit?: number;
  skip?: boolean;
}

export function useTitlesByLocationQuery({ location, limit, skip = false }: UseTitlesByLocationQueryOptions) {
  const { data, loading, ...queryResult } = useQuery<TitlesByLocationQueryData, TitlesByLocationQueryVariables>(
    titlesByLocation,
    {
      variables: {
        location: location!,
        limit,
      },
      skip: !location || skip,
      client: routedClient,
    }
  );

  const titles: Title[] = useMemo(() => (data ? data.titlesByLocation : []), [data]);

  return { titlesByLocation: titles, queryResult, loading };
}
